import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useStore } from "../../../stores/store";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useSetMenu } from "../../../hooks/useSetMenu";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    Row, Col, Button, Tabs, Tab,
    Form, Accordion, Alert, Spinner
} from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import FormSelect from '../../Generic/FormSelect';
import confirmDeleteDialog from '../../Generic/confirmDeleteDialog';
import './CriteriaManager.css';

// Icons for visual enhancement
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/Settings';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

const Manager = () => {
    const { mainStore, criteriaStore, criteriaValueStore } = useStore();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    useSetMenu(mainStore.CriteriaManagerMenuType, mainStore.CriteriaManagerMenuNo);

    // Tab state
    const [activeTab, setActiveTab] = useState("criteria-list");

    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [criteriaAdded, setCriteriaAdded] = useState(false);

    // Criteria form state
    const [weightCoefficients, setWeightCoefficients] = useState([
        { value: 1.0, label: '1.0' },
        { value: 0.5, label: '0.5' },
        { value: 0.33, label: '0.33' }
    ]);

    // Filter state
    const [panels, setPanels] = useState([]);
    const [selectedPanel, setSelectedPanel] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [criterias, setCriterias] = useState([]);
    const [selectedCriteria, setSelectedCriteria] = useState(null);

    // Value batch state
    const [batchValues, setBatchValues] = useState("");
    const [parsedValues, setParsedValues] = useState([]);
    const [criteriaValues, setCriteriaValues] = useState([]);

    // Criteria Form validation
    const criteriaSchema = yup.object({
        name: yup
            .string()
            .required(t('requiredField'))
            .max(100, t('maxLength', { max: 100 })),
        subcategoryId: yup.string().required(t("selectRequired")),
        panelId: yup.string().required(t("selectRequired")),
        categoryId: yup.string().required(t("selectRequired")),
        weightCoefficient: yup.string().required(t("selectRequired")),
    });

    const {
        register: criteriaRegister,
        handleSubmit: handleCriteriaSubmit,
        setValue: setCriteriaValue,
        reset: resetCriteriaForm,
        watch: criteriaWatch,
        formState: { errors: criteriaErrors },
    } = useForm({
        resolver: yupResolver(criteriaSchema),
        defaultValues: {
            weightCoefficient: "",
            isMandatory: false
        }
    });

    // Watch isMandatory and weightCoefficient for auto-linking
    const isMandatory = criteriaWatch("isMandatory");
    const weightCoefficient = criteriaWatch("weightCoefficient");

    // Auto-set weight coefficient to 1.0 when isMandatory is checked
    useEffect(() => {
        if (isMandatory) {
            setCriteriaValue("weightCoefficient", "1", { shouldValidate: true });
        }
    }, [isMandatory, setCriteriaValue]);

    // Auto-set isMandatory based on weight coefficient
    useEffect(() => {
        if (weightCoefficient === "1" || weightCoefficient === 1) {
            setCriteriaValue("isMandatory", true);
        } else if (weightCoefficient) {
            setCriteriaValue("isMandatory", false);
        }
    }, [weightCoefficient, setCriteriaValue]);

    // Process batch values into array
    useEffect(() => {
        if (batchValues.trim()) {
            // Split by newlines, commas, or semicolons
            const values = batchValues
                .split(/[\n,;]/)
                .map(val => val.trim())
                .filter(val => val.length > 0 && val.length <= 250);

            setParsedValues(values);
        } else {
            setParsedValues([]);
        }
    }, [batchValues]);

    // Initial data load
    useEffect(() => {
        loadPanels();
        // eslint-disable-next-line
    }, []);

    // Load values for selected criteria
    useEffect(() => {
        if (selectedCriteria) {
            loadCriteriaValues(selectedCriteria.value);
        } else {
            setCriteriaValues([]);
        }
    }, [selectedCriteria]);

    // Load panels for dropdowns
    const loadPanels = async () => {
        try {
            const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
            setPanels(panelsResponse.data);
        } catch (err) {
            enqueueSnackbar(t("loadDataError"), { variant: "error" });
        }
    };

    // Load criteria values for selected criteria
    const loadCriteriaValues = async (criteriaId) => {
        try {
            setIsLoading(true);
            const response = await axiosPrivate.get(`/CriteriaValues/Filtered?criteriaId=${criteriaId}`);
            if (Array.isArray(response.data)) {
                setCriteriaValues(response.data);
            } else {
                setCriteriaValues([]);
            }
        } catch (error) {
            enqueueSnackbar(t("errorLoadingData"), { variant: "error" });
            setCriteriaValues([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Create new criteria
    const onSubmitCriteria = async (data) => {
        try {
            setIsLoading(true);
            const response = await axiosPrivate.post("/Criterias", data);

            if (response && response.data && response.data.id) {
                enqueueSnackbar(t("criteriaCreateSuccess"), { variant: "success" });
                resetCriteriaForm();
                setCriteriaAdded(true);

                // Update the criteria dropdown and select the new one
                if (selectedSubcategory) {
                    const criteriasResponse = await axiosPrivate.get(`/Criterias/SelectOptionsBySubcategory/${selectedSubcategory.value}`);
                    if (typeof criteriasResponse.data === "string" && criteriasResponse.data === "No records") {
                        setCriterias([]);
                    } else {
                        // Add the new criteria to the list with its details
                        const newCriteria = {
                            value: response.data.id.toString(),
                            label: response.data.name,
                            weightCoefficient: response.data.weightCoefficient, // Ensure this is included
                            isMandatory: response.data.isMandatory // Ensure this is included
                        };
                        setCriterias(prevCriterias => [...prevCriterias, newCriteria]);
                        setDetailedCriteriasData(prevData => [...prevData, {
                            id: response.data.id,
                            weightCoefficient: response.data.weightCoefficient,
                            isMandatory: response.data.isMandatory,
                            label: response.data.name
                        }]); // Add new criteria details to detailedCriteriasData
                        setSelectedCriteria(newCriteria); // Select the new criteria
                    }
                }

                // Switch to the values tab
                setActiveTab("criteria-values");
            } else {
                enqueueSnackbar(t("criteriaCreateError"), { variant: "error" });
            }
        } catch (error) {
            enqueueSnackbar(t("criteriaCreateError"), { variant: "error" });
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    // Delete criteria and its values
    const deleteCriteria = async (id) => {
        try {
            const response = await axiosPrivate.delete(`/Criterias/${id}`);
            if (response.status === 200) {
                enqueueSnackbar(t("deleteMessageSuccess"), { variant: "success" });

                // Update the criteria list in the state
                setCriterias(prevCriterias => prevCriterias.filter(criteria => criteria.value !== id.toString()));

                // If the deleted criteria was selected, clear the selected criteria and its values
                if (selectedCriteria && selectedCriteria.value === id.toString()) {
                    setSelectedCriteria(null); // Clear the selected criteria
                    setCriteriaValues([]); // Clear the criteria values
                }
            } else {
                throw new Error("Delete operation failed");
            }
        } catch (error) {
            if (error.response && error.response.status === 403) {
                enqueueSnackbar(t("deleteCannotDeleteIsUsed"), { variant: "error" });
            } else {
                enqueueSnackbar(t("deleteMessageError"), { variant: "error" });
                console.error("Delete error:", error);
            }
        }
    };

    // Delete criteria value
    const deleteCriteriaValue = async (id) => {
        try {
            const response = await axiosPrivate.delete(`/CriteriaValues/${id}`);
            if (response.status === 200) {
                enqueueSnackbar(t("deleteMessageSuccess"), { variant: "success" });

                // Update the criteria values in the state
                setCriteriaValues(prevValues => prevValues.filter(value => value.id !== id));

                // Refresh values list if a criteria is selected
                if (selectedCriteria) {
                    loadCriteriaValues(selectedCriteria.value); // Reload values for the currently selected criteria
                }
            } else {
                throw new Error("Delete operation failed");
            }
        } catch (error) {
            if (error.response && error.response.status === 403) {
                enqueueSnackbar(t("deleteCannotDeleteIsUsed"), { variant: "error" });
            } else {
                enqueueSnackbar(t("deleteMessageError"), { variant: "error" });
                console.error("Delete error:", error);
            }
        }
    };

    // Confirm delete criteria dialog
    const confirmDeleteCriteria = useCallback((id) => {
        confirmDeleteDialog(t, deleteCriteria, id);
    }, [t]);

    // Confirm delete criteria value dialog
    const confirmDeleteCriteriaValue = useCallback((id) => {
        confirmDeleteDialog(t, deleteCriteriaValue, id);
    }, [t]);

    // Add batch values for selected criteria
    const handleAddValues = async () => {
        if (!selectedCriteria) {
            enqueueSnackbar(t("selectCriteriaFirst"), { variant: "warning" });
            return;
        }

        if (parsedValues.length === 0) {
            enqueueSnackbar(t("pleaseAddAtLeastOneValue"), { variant: "warning" });
            return;
        }

        try {
            setIsLoading(true);

            const batchRequest = {
                criteriaId: parseInt(selectedCriteria.value, 10),
                panelId: parseInt(selectedPanel.value, 10),
                categoryId: parseInt(selectedCategory.value, 10),
                subcategoryId: parseInt(selectedSubcategory.value, 10),
                values: parsedValues
            };

            await axiosPrivate.post("/CriteriaValues/BatchCreate", batchRequest);
            enqueueSnackbar(t("batchCreateSuccess"), { variant: "success" });
            setBatchValues("");

            // Refresh values list
            loadCriteriaValues(selectedCriteria.value);
        } catch (error) {
            enqueueSnackbar(t("batchCreateError"), { variant: "error" });
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    // Update criteria value active status
    const toggleCriteriaValueActive = async (value, isActive) => {
        try {
            setIsLoading(true);
            await axiosPrivate.put(`/CriteriaValues/${value.id}`, {
                ...value,
                isActive: !isActive
            });
            enqueueSnackbar(t("updateMessageSuccess"), { variant: "success" });
            // Refresh values list
            if (selectedCriteria) {
                loadCriteriaValues(selectedCriteria.value);
            }
        } catch (error) {
            enqueueSnackbar(t("updateMessageError"), { variant: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    // Load detailed criteria data (with weightCoefficient and IsMandatory)
    const loadDetailedCriterias = async (subcategoryId) => {
        try {
            setIsLoading(true);
            const response = await axiosPrivate.get(`/Criterias/Filtered?subcategoryId=${subcategoryId}`);
            if (Array.isArray(response.data)) {
                return response.data;
            } else {
                return [];
            }
        } catch (error) {
            enqueueSnackbar(t("errorLoadingCriteria"), { variant: "error" });
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    // Dropdown change handlers
    const handlePanelChange = async (selectedOption) => {
        setCriteriaValue("panelId", selectedOption ? selectedOption.value : null, { shouldValidate: true });
        setSelectedPanel(selectedOption);

        if (!selectedOption) {
            setCategories([]);
            setSubcategories([]);
            setCriterias([]);

            setSelectedCategory(null);
            setSelectedSubcategory(null);
            setSelectedCriteria(null);
            return;
        }

        try {
            const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setCategories([]);
            } else {
                setCategories(response.data);
            }

            setSubcategories([]);
            setCriterias([]);

            setSelectedCategory(null);
            setSelectedSubcategory(null);
            setSelectedCriteria(null);

            setCriteriaValue("categoryId", null, { shouldValidate: false });
            setCriteriaValue("subcategoryId", null, { shouldValidate: false });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
        }
    };

    const handleCategoryChange = async (selectedOption) => {
        setCriteriaValue("categoryId", selectedOption ? selectedOption.value : null, { shouldValidate: true });
        setSelectedCategory(selectedOption);

        if (!selectedOption) {
            setSubcategories([]);
            setCriterias([]);

            setSelectedSubcategory(null);
            setSelectedCriteria(null);
            return;
        }

        try {
            const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setSubcategories([]);
            } else {
                setSubcategories(response.data);
            }

            setCriterias([]);

            setSelectedSubcategory(null);
            setSelectedCriteria(null);

            setCriteriaValue("subcategoryId", null, { shouldValidate: false });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
        }
    };

    const handleSubcategoryChange = async (selectedOption) => {
        setCriteriaValue("subcategoryId", selectedOption ? selectedOption.value : null, { shouldValidate: true });
        setSelectedSubcategory(selectedOption);

        if (!selectedOption) {
            setCriterias([]);
            setSelectedCriteria(null);
            return;
        }

        try {
            // First get the basic options for the dropdown
            const dropdownResponse = await axiosPrivate.get(`/Criterias/SelectOptionsBySubcategory/${selectedOption.value}`);
            if (typeof dropdownResponse.data === "string" && dropdownResponse.data === "No records") {
                setCriterias([]);
            } else {
                setCriterias(dropdownResponse.data);
            }

            setSelectedCriteria(null);
        } catch (error) {
            enqueueSnackbar(t("errorLoadingCriteria"), { variant: "error" });
        }
    };

    const handleCriteriaChange = (selectedOption) => {
        setSelectedCriteria(selectedOption);
    };

    const handleWeightCoefficientChange = selectedOption => {
        setCriteriaValue("weightCoefficient", selectedOption.value, { shouldValidate: true });
    };

    // Load detailed criteria data when subcategory changes
    useEffect(() => {
        if (selectedSubcategory) {
            loadDetailedCriterias(selectedSubcategory.value).then(detailedCriterias => {
                // Store the detailed info in a ref or state if needed
                // We'll use this for displaying in the criteria list
                setDetailedCriteriasData(detailedCriterias);
            });
        } else {
            setDetailedCriteriasData([]);
        }
    }, [selectedSubcategory]);

    // State to store detailed criteria data
    const [detailedCriteriasData, setDetailedCriteriasData] = useState([]);

    // Helper function to get weight badge class and label
    const getWeightBadgeInfo = (weight) => {
        if (weight === 1 || weight === 1.0) {
            return { className: 'weight-badge weight-badge-1', label: 'W = 1.00' };
        } else if (weight === 0.5) {
            return { className: 'weight-badge weight-badge-05', label: 'W = 0.50' };
        } else if (weight === 0.33) {
            return { className: 'weight-badge weight-badge-033', label: 'W = 0.33' };
        }
        return { className: 'weight-badge', label: `W = ${weight}` };
    };

    // Calculate statistics for summary cards
    const stats = useMemo(() => {
        const total = detailedCriteriasData.length;
        const mandatory = detailedCriteriasData.filter(c => c.isMandatory).length;
        const w100 = detailedCriteriasData.filter(c => c.weightCoefficient === 1 || c.weightCoefficient === 1.0).length;
        const w050 = detailedCriteriasData.filter(c => c.weightCoefficient === 0.5).length;
        const w033 = detailedCriteriasData.filter(c => c.weightCoefficient === 0.33).length;
        return { total, mandatory, w100, w050, w033 };
    }, [detailedCriteriasData]);

    return (
        <div className="criteria-manager-container">
            {/* Page Header */}
            <div className="criteria-manager-header">
                <h2>
                    <span className="icon">
                        <SettingsIcon />
                    </span>
                    {t("integratedCriteriaManager")}
                </h2>
                <Button
                    className="btn-back"
                    onClick={() => navigate(-1)}
                >
                    <ArrowBackIcon style={{ fontSize: 18, marginRight: 6 }} />
                    {t("cancel")}
                </Button>
            </div>

            {/* Filter Card */}
            <div className="filter-card">
                <div className="filter-card-header">
                    <FilterListIcon />
                    <h5>{t("filters")}</h5>
                </div>
                <div className="filter-card-body">
                    <Row>
                        <FormSelect
                            label="panel"
                            name="panelId"
                            register={criteriaRegister}
                            errors={criteriaErrors}
                            options={panels}
                            onChange={handlePanelChange}
                            value={selectedPanel}
                            isClearable={true}
                            colSize={3}
                        />
                        <FormSelect
                            label="category"
                            name="categoryId"
                            register={criteriaRegister}
                            errors={criteriaErrors}
                            options={categories}
                            onChange={handleCategoryChange}
                            value={selectedCategory}
                            isClearable={true}
                            colSize={3}
                        />
                        <FormSelect
                            label="subcategory"
                            name="subcategoryId"
                            register={criteriaRegister}
                            errors={criteriaErrors}
                            options={subcategories}
                            onChange={handleSubcategoryChange}
                            value={selectedSubcategory}
                            isClearable={true}
                            colSize={3}
                        />
                        <FormSelect
                            label="criteria"
                            name="criteriaId"
                            options={criterias}
                            onChange={handleCriteriaChange}
                            value={selectedCriteria}
                            isClearable={true}
                            colSize={3}
                        />
                    </Row>
                </div>
            </div>

            {/* Statistics Summary Cards - Show when subcategory is selected */}
            {selectedSubcategory && detailedCriteriasData.length > 0 && (
                <div className="stats-summary">
                    <div className="stat-card">
                        <div className="stat-icon blue">
                            <ListAltIcon />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.total}</div>
                            <div className="stat-label">{t("totalCriteria")}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon red">
                            <WarningIcon />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.w100}</div>
                            <div className="stat-label">W = 1.00</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon amber">
                            <CheckCircleIcon />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.w050}</div>
                            <div className="stat-label">W = 0.50</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon green">
                            <CheckCircleIcon />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.w033}</div>
                            <div className="stat-label">W = 0.33</div>
                        </div>
                    </div>
                </div>
            )}

            <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="criteria-tabs"
            >
                <Tab eventKey="criteria-list" title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <ListAltIcon style={{ fontSize: 18 }} />
                        {t("criterias")}
                    </span>
                }>
                    {criteriaAdded && (
                        <Alert variant="success" className="criteria-alert" onClose={() => setCriteriaAdded(false)} dismissible>
                            <CheckCircleIcon style={{ marginRight: 8 }} />
                            {t("criteriaCreateSuccess")}
                        </Alert>
                    )}

                    <Accordion defaultActiveKey="0" className="create-criteria-accordion">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <AddCircleOutlineIcon style={{ marginRight: 10 }} />
                                {t("criteriaCreateTitle")}
                            </Accordion.Header>
                            <Accordion.Body>
                                <Form onSubmit={handleCriteriaSubmit(onSubmitCriteria)}>
                                    <Row>
                                        <Col md={12}>
                                            <Form.Group className="criteria-form-group">
                                                <Form.Label>{t("criteria")}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder={t("enterCriteriaName")}
                                                    isInvalid={!!criteriaErrors.name}
                                                    {...criteriaRegister("name")}
                                                />
                                                {criteriaErrors.name && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {criteriaErrors.name.message}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="criteria-form-group">
                                                <Form.Label>{t("criteriaWeightCoefficient")}</Form.Label>
                                                <Form.Select
                                                    isInvalid={!!criteriaErrors.weightCoefficient}
                                                    {...criteriaRegister("weightCoefficient")}
                                                    onChange={(e) => {
                                                        const selected = weightCoefficients.find(wc => wc.value.toString() === e.target.value);
                                                        handleWeightCoefficientChange(selected);
                                                    }}
                                                >
                                                    <option value="">{t("selectRequired")}</option>
                                                    {weightCoefficients.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            W = {option.value === 1 ? '1.00' : option.value === 0.5 ? '0.50' : '0.33'}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                {criteriaErrors.weightCoefficient && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {criteriaErrors.weightCoefficient.message}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="criteria-form-group">
                                                <Form.Label>{t("isMandatory")}</Form.Label>
                                                <div style={{
                                                    background: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '8px',
                                                    padding: '10px 14px',
                                                    marginTop: '4px'
                                                }}>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="isMandatory"
                                                            style={{ width: '18px', height: '18px' }}
                                                            {...criteriaRegister("isMandatory")}
                                                            disabled={weightCoefficient === "1" || weightCoefficient === 1}
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor="isMandatory"
                                                            style={{ marginLeft: '8px', fontWeight: 500, color: '#374151' }}
                                                        >
                                                            {t("yes")} - {t("mandatoryCriteria")}
                                                        </label>
                                                    </div>
                                                </div>
                                                <Form.Text className="text-muted">
                                                    {t("weightCoefficientHint")}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="d-flex justify-content-end mt-3">
                                        <Button
                                            className="btn-primary-gradient"
                                            type="submit"
                                            disabled={isLoading || !selectedSubcategory}
                                        >
                                            {isLoading ? (
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                            ) : (
                                                <>
                                                    <AddCircleOutlineIcon style={{ fontSize: 18, marginRight: 6 }} />
                                                    {t("save")}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>

                    {selectedSubcategory && (
                        <div className="criteria-list-section">
                            <h5>{t("criterias")} - {selectedSubcategory.label}</h5>
                            {criterias.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-state-icon">
                                        <ListAltIcon style={{ fontSize: '3rem', color: '#cbd5e1' }} />
                                    </div>
                                    <p className="empty-state-text">{t("noCriteriaFound")}</p>
                                </div>
                            ) : (
                                <div>
                                    {criterias.map(criteria => {
                                        const detailedCriteria = detailedCriteriasData.find(
                                            dc => dc.id.toString() === criteria.value
                                        );
                                        const weightInfo = detailedCriteria
                                            ? getWeightBadgeInfo(detailedCriteria.weightCoefficient)
                                            : null;

                                        return (
                                            <div
                                                key={criteria.value}
                                                className="criteria-item"
                                                onClick={() => {
                                                    setSelectedCriteria(criteria);
                                                    setActiveTab("criteria-values");
                                                }}
                                            >
                                                <div className="criteria-item-content">
                                                    <div className="criteria-item-header">
                                                        <h6 className="criteria-item-name">{criteria.label}</h6>
                                                        {detailedCriteria && detailedCriteria.isMandatory && (
                                                            <span className="mandatory-badge">
                                                                {t("mandatory")}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {weightInfo && (
                                                        <div className="criteria-item-meta">
                                                            <span className={weightInfo.className}>
                                                                {weightInfo.label}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="criteria-item-actions">
                                                    <button
                                                        className="btn-values"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedCriteria(criteria);
                                                            setActiveTab("criteria-values");
                                                        }}
                                                    >
                                                        <ListAltIcon style={{ fontSize: 16 }} />
                                                        {t("criteriaValues")}
                                                    </button>
                                                    <button
                                                        className="btn-delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            confirmDeleteCriteria(criteria.value);
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon style={{ fontSize: 20 }} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </Tab>

                <Tab
                    eventKey="criteria-values"
                    title={
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <AddCircleOutlineIcon style={{ fontSize: 18 }} />
                            {t("criteriaValues")}
                        </span>
                    }
                    disabled={!selectedCriteria}
                >
                    {selectedCriteria ? (
                        <>
                            <h5 className="values-section-title">
                                {t("criteriaValues")} - {selectedCriteria.label}
                            </h5>

                            {/* Batch Add Values Card */}
                            <div className="batch-values-card">
                                <div className="batch-values-header">
                                    <AddCircleOutlineIcon />
                                    {t("addMultipleValues")}
                                </div>
                                <div className="batch-values-body">
                                    <Form.Group className="criteria-form-group">
                                        <Form.Label style={{ fontWeight: 500, color: '#374151' }}>
                                            {t("batchValues")}
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            className="batch-textarea"
                                            value={batchValues}
                                            onChange={(e) => setBatchValues(e.target.value)}
                                            placeholder={t("enterValuesSeperatedBy")}
                                        />
                                        <Form.Text style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                            {t("batchValuesHelp")}
                                        </Form.Text>
                                    </Form.Group>

                                    {parsedValues.length > 0 && (
                                        <div className="value-preview">
                                            <div className="value-preview-title">
                                                {t("valuePreview")}
                                                <span className="value-preview-count">{parsedValues.length}</span>
                                            </div>
                                            <div className="value-tags">
                                                {parsedValues.map((val, index) => (
                                                    <span className="value-tag" key={index}>
                                                        {val}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-end mt-3">
                                        <Button
                                            className="btn-primary-gradient"
                                            onClick={handleAddValues}
                                            disabled={isLoading || parsedValues.length === 0}
                                        >
                                            {isLoading ? (
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                            ) : (
                                                <>
                                                    <AddCircleOutlineIcon style={{ fontSize: 18, marginRight: 6 }} />
                                                    {t("addValues")}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Values List Card */}
                            <div className="values-list-card">
                                <div className="values-list-header">
                                    <ListAltIcon />
                                    {t("criteriaValueListTitle")}
                                    {criteriaValues.length > 0 && (
                                        <span style={{
                                            background: 'rgba(255,255,255,0.2)',
                                            padding: '2px 10px',
                                            borderRadius: '12px',
                                            fontSize: '0.85rem',
                                            marginLeft: '8px'
                                        }}>
                                            {criteriaValues.length}
                                        </span>
                                    )}
                                </div>
                                <div className="values-list-body">
                                    {isLoading ? (
                                        <div className="loading-state">
                                            <Spinner animation="border" role="status" style={{ color: '#3b82f6' }}>
                                                <span className="visually-hidden">{t("loading")}</span>
                                            </Spinner>
                                        </div>
                                    ) : criteriaValues.length === 0 ? (
                                        <div className="empty-state">
                                            <div className="empty-state-icon">
                                                <ListAltIcon style={{ fontSize: '3rem', color: '#cbd5e1' }} />
                                            </div>
                                            <p className="empty-state-text">{t("noCriteriaValuesFound")}</p>
                                        </div>
                                    ) : (
                                        <div>
                                            {criteriaValues.map(value => (
                                                <div
                                                    key={value.id}
                                                    className={`value-item ${!value.isActive ? 'inactive' : ''}`}
                                                >
                                                    <div className="value-item-name">
                                                        {value.value}
                                                        {!value.isActive && (
                                                            <span style={{
                                                                marginLeft: '10px',
                                                                fontSize: '0.75rem',
                                                                color: '#94a3b8',
                                                                fontStyle: 'italic'
                                                            }}>
                                                                ({t("inactive")})
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="value-item-actions">
                                                        <button
                                                            className={`btn-toggle-active ${value.isActive ? 'active' : 'inactive'}`}
                                                            onClick={() => toggleCriteriaValueActive(value, value.isActive)}
                                                        >
                                                            {value.isActive ? (
                                                                <>
                                                                    <VisibilityOffIcon style={{ fontSize: 16, marginRight: 4 }} />
                                                                    {t("deactivate")}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <VisibilityIcon style={{ fontSize: 16, marginRight: 4 }} />
                                                                    {t("activate")}
                                                                </>
                                                            )}
                                                        </button>
                                                        <button
                                                            className="btn-delete"
                                                            onClick={() => confirmDeleteCriteriaValue(value.id)}
                                                        >
                                                            <DeleteOutlineIcon style={{ fontSize: 20 }} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <Alert variant="warning" className="criteria-alert">
                            <WarningIcon style={{ marginRight: 8 }} />
                            {t("selectCriteriaFirst")}
                        </Alert>
                    )}
                </Tab>
            </Tabs>
        </div>
    );
};

export default observer(Manager); 